import { useContext, useMemo } from "react";
import { DynamicContext } from "../../../Context/DynamicContext";
import "./Billings.css";
import {
  UPDATE_SUBSCRIPTION_PLAN,
  VYLABS_BASE_URL,
} from "../../../helper/url_helper";
import { post } from "../../../helper/Api_helper";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar";
import Button from "../../../Common/Button/Button";
import usersQuotaIcon from "../../../Assets/Images/quota-card-user.svg";
import monthlyQuotaIcon from "../../../Assets/Images/quota-card-monthly.svg";
import crownIcon from "../../../Assets/Images/crown-icon.svg";

function QuotaCard({ icon, title, subtitle, badge, resetInfo,stats = [], percentage = 0 }) {
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const clampedPercentage = Math.max(0, Math.min(percentage || 0, 100));
  const dashOffset = circumference - (clampedPercentage / 100) * circumference;

  return (
    <div className="quota-card-3d fade-in-up">
      <div className="quota-card-header-3d">
        <div className="quota-header-left-3d">
          <div className="quota-icon-3d">{icon}</div>
          <div className="quota-heading-text-3d">
            <h4>{title}</h4>
            <p>{subtitle}</p>
          </div>
        </div>
         {resetInfo && (
          <div
            className={`quota-reset-3d${
              resetInfo.exhausted ? " quota-reset-exhausted-3d" : ""
            }`}
          >
            <div className="quota-reset-icon-3d">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 1 1-3-6.7" />
                <polyline points="21 3 21 9 15 9" />
              </svg>
            </div>
            <div className="quota-reset-text-3d">
              <span className="quota-reset-title-3d">
                {resetInfo.exhausted ? "Quota exhausted" : "Quota resets on"}
              </span>
              <strong className="quota-reset-date-3d">{resetInfo.date}</strong>
              <span className="quota-reset-sub-3d">
                {resetInfo.exhausted
                  ? `You can send emails again ${resetInfo.daysLabel}`
                  : `New quota starts ${resetInfo.daysLabel}`}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="quota-stats-row-3d">
        {stats.map((stat, index) => (
          <div className="quota-stat-3d" key={index}>
            <span className="quota-stat-label-3d">{stat.label}</span>
            <span
              className={`quota-stat-value-3d${
                stat.highlight ? " quota-stat-value-highlight-3d" : ""
              }${stat.accent ? " quota-stat-value-accent-3d" : ""}`}
            >
              {stat.value}
            </span>
            {stat.unit && (
              <span className="quota-stat-unit-3d">{stat.unit}</span>
            )}
          </div>
        ))}

        <div className="quota-ring-3d">
          <svg width="76" height="76" viewBox="0 0 76 76">
            <circle
              className="quota-ring-track-3d"
              cx="38"
              cy="38"
              r={radius}
              fill="none"
              strokeWidth="6"
            />
            <circle
              className="quota-ring-fill-3d"
              cx="38"
              cy="38"
              r={radius}
              fill="none"
              strokeWidth="6"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
              transform="rotate(-90 38 38)"
            />
          </svg>
          <div className="quota-ring-text-3d">
            <span className="quota-ring-value-3d">
              {Math.round(clampedPercentage)}%
            </span>
            <span className="quota-ring-label-3d">Used</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Billings({ plansData = [], isManualBilling }) {
  const { subscriptionDetails, userTimezone } =
    useContext(DynamicContext);

  const getPreferredCurrency = (timezone) => {
    const timezoneToCurrency = {
      "America/New_York": "USD",
      "America/Los_Angeles": "USD",
      "America/Chicago": "USD",
      "America/Toronto": "CAD",
      "America/Vancouver": "CAD",
      "Asia/Kolkata": "INR",
      "Asia/Calcutta": "INR",
    };
    if (timezoneToCurrency[timezone]) {
      return timezoneToCurrency[timezone];
    }
    if (
      timezone.includes("America/Toronto") ||
      timezone.includes("America/Vancouver")
    ) {
      return "CAD";
    } else if (
      timezone.includes("Asia/Kolkata") ||
      timezone.includes("Asia/Calcutta")
    ) {
      return "INR";
    } else if (timezone.includes("America/")) {
      const canadianZones = ["Toronto", "Vancouver", "Montreal", "Ottawa"];
      const isCanadian = canadianZones.some((zone) => timezone.includes(zone));
      return isCanadian ? "CAD" : "USD";
    }
    return "USD";
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return "0";
    const amountInUnits = amount / 100;

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return formatter.format(amountInUnits);
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      USD: "$",
      INR: "₹",
      CAD: "C$",
      EUR: "€",
      GBP: "£",
    };
    return symbols[currency] || currency || "$";
  };

  const currentPlan = useMemo(() => {
    if (!subscriptionDetails?.plan_name || !plansData?.length) return null;

    return plansData.find(
      (plan) =>
        plan.name.toLowerCase() === subscriptionDetails.plan_name.toLowerCase(),
    );
  }, [subscriptionDetails?.plan_name, plansData]);

  const getDefaultPrice = (plan) => {
    if (!plan || !plan.prices || plan.prices.length === 0) return null;
    const preferredCurrency = getPreferredCurrency(userTimezone);
    const preferredPrice = plan.prices.find(
      (price) => price.currency === preferredCurrency,
    );
    const usdPrice = plan.prices.find((price) => price.currency === "USD");
    return preferredPrice || usdPrice || plan.prices[0];
  };

  const getPlanPrice = () => {
    const defaultPrice = getDefaultPrice(currentPlan);
    if (!defaultPrice) return "0";

    return formatCurrency(defaultPrice.price_amount, defaultPrice.currency);
  };

  const getPlanCurrencySymbol = () => {
    const defaultPrice = getDefaultPrice(currentPlan);
    if (!defaultPrice) return "$";

    const currency = defaultPrice.currency;
    return getCurrencySymbol(currency);
  };

  const getBillingInterval = () => {
    const defaultPrice = getDefaultPrice(currentPlan);
    return defaultPrice?.billing_interval || "month";
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "1970-01-01T00:00:00Z") {
      return "N/A";
    }
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatPlanName = (planName) => {
    if (!planName) return "Current Plan";
    return planName.charAt(0).toUpperCase() + planName.slice(1) + " Plan";
  };

  const getPlanDescription = (planName) => {
    const descriptions = {
      starter:
        "Perfect for small teams getting started with resume processing.",
      professional:
        "Ideal for growing teams with advanced resume processing needs.",
      enterprise: "For large organizations with custom requirements.",
    };
    return (
      descriptions[planName] || "High-quality resume processing for your team."
    );
  };

  const getFeatureValue = (featureName) => {
    const feature = subscriptionDetails?.features?.find(
      (f) => f.name === featureName,
    );
    return feature || null;
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === -1 || limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const maxUsersFeature = getFeatureValue("max_users");
  const monthlyEmailsFeature = getFeatureValue("monthly_emails");
  const getResetInfo = (feature) => {
    const periodEnd = subscriptionDetails?.current_period_end;
    if (!feature || !periodEnd || periodEnd === "1970-01-01T00:00:00Z") return null;

    const msLeft = new Date(periodEnd).getTime() - Date.now();
    const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));

    let daysLabel;
    if (daysLeft <= 0) daysLabel = "shortly";
    else if (daysLeft === 1) daysLabel = "tomorrow";
    else daysLabel = `in ${daysLeft} days`;

    const hasLimit = feature.limit > 0;
    return {
      date: formatDate(periodEnd),
      daysLabel,
      exhausted: hasLimit && (feature.used || 0) >= feature.limit,
    };
  };

  const emailResetInfo = getResetInfo(monthlyEmailsFeature);
  const planName = subscriptionDetails?.plan_name;
  const planPrice = getPlanPrice();
  const planCurrencySymbol = getPlanCurrencySymbol();
  const billingInterval = getBillingInterval();
  const isTrial = subscriptionDetails?.status === "trialing";
  const subscriptionStartLabel = isTrial ? "Trial Start" : "Start Date";
  const subscriptionEndLabel = isTrial ? "Trial End" : "End Date";
  const subscriptionStartDate = isTrial
    ? subscriptionDetails?.trial_start
    : subscriptionDetails?.start_date;
  const subscriptionEndDate = isTrial
    ? subscriptionDetails?.trial_end
    : subscriptionDetails?.end_date;

  const getStatusBadgeClass = (status) => {
    const baseClass = "status-badge-3d";
    switch (status) {
      case "trialing":
        return `${baseClass} status-trialing-3d`;
      case "active":
        return `${baseClass} status-active-3d`;
      default:
        return `${baseClass} status-active-3d`;
    }
  };

  const handleUpdatePlan = async () => {
    try {
      const response = await post(VYLABS_BASE_URL, UPDATE_SUBSCRIPTION_PLAN);
      if (response && response.portal_session_url) {
       window.location.href = response.portal_session_url;
      } else {
        console.error("No portal session URL received");
        Toast("error", "Failed to get portal session URL");
      }
    } catch (error) {
      console.error("Error updating subscription plan:", error);
      Toast("error", error.message || "An error occurred");
    }
  };

  const handleCustomPlanClick = () => {
    window.location.href = "mailto:dm@vylabs.ai";
  };

  return (
    <div className="billings-container">
      <div className="plan-header-3d fade-in-up">
        <div className="plan-title-container">
          <h1 className="plan-title-3d">
            <img src={crownIcon} alt="Active" style={{ 
              width: "20px", 
              background: "#FFFFFF26",
              padding: "15px",
              borderRadius: "100px" }} />
            {formatPlanName(planName)}
          </h1>
          <p className="plan-description-3d">{getPlanDescription(planName)}</p>
        </div>
        { isManualBilling ? (
        <Button
          label="Contact us"
          event={handleCustomPlanClick}
          style={{
            background:
              "linear-gradient(135deg, #7367FF 0%, #8B85F5 50%, #7367FF 100%)",
            color: "white",
            border: "2px solid rgba(255, 255, 255, 0.3)",
            padding: "14px 28px",
            borderRadius: "50px",
            fontSize: "16px",
            fontWeight: "700",
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "relative",
            overflow: "hidden",
            zIndex: "10",
            boxShadow:
              "0 0 20px rgba(115, 103, 255, 0.7), " +
              "0 10px 25px rgba(115, 103, 255, 0.5), " +
              "inset 0 1px 0 rgba(255, 255, 255, 0.4)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
          }}
        />
        ) : (
          <Button
          label="Manage subscription"
          event={handleUpdatePlan}
          style={{
            background:
              "linear-gradient(135deg, #7367FF 0%, #8B85F5 50%, #7367FF 100%)",
            color: "white",
            border: "2px solid rgba(255, 255, 255, 0.3)",
            padding: "14px 28px",
            borderRadius: "50px",
            fontSize: "16px",
            fontWeight: "700",
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "relative",
            overflow: "hidden",
            zIndex: "10",
            boxShadow:
              "0 0 20px rgba(115, 103, 255, 0.7), " +
              "0 10px 25px rgba(115, 103, 255, 0.5), " +
              "inset 0 1px 0 rgba(255, 255, 255, 0.4)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
          }}
        />
        )}
      </div>

      <div className="billing-cards-3d">
        <div className="billing-card-3d glow-on-hover fade-in-up">
          {/* <div className="card-header-3d">
            <div className="card-icon-3d">
              <CreditCardIcon />
            </div>
            <h3>Billing Information</h3>
          </div> */}
          <div className="pricing-section-3d">
            {!isManualBilling && (
              <h1 className="pricing-3d">
                <span className="currency-3d">{planCurrencySymbol}</span>
                {planPrice.replace(planCurrencySymbol, "").trim()}
                <span className="period-3d">/{billingInterval}</span>
              </h1>
            )}
            <div className="billing-details-3d">
              <div className="billing-item-3d">
                <span>Status:</span>
                <span
                  className={getStatusBadgeClass(subscriptionDetails?.status)}
                >
                  {subscriptionDetails?.status || "Active"}
                </span>
              </div>
              <div className="billing-item-3d">
                <span>{subscriptionStartLabel}:</span>
                <strong>{formatDate(subscriptionStartDate)}</strong>
              </div>
              <div className="billing-item-3d">
                <span>{subscriptionEndLabel}:</span>
                <strong>{formatDate(subscriptionEndDate)}</strong>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div className="quota-section-3d">
        {maxUsersFeature?.toggle && (
          <QuotaCard
            icon={ <img src={usersQuotaIcon} alt="usersQuotaIcon" />}
            title="User Quota (Organization)"
            subtitle="Monitor and manage your users."
            stats={[
              {
                label: "Users Created",
                value: (maxUsersFeature?.used || 0),
                unit: maxUsersFeature?.used <= 1 ? "user" : "users",
              },
              {
                label: "Max Users",
                value: (maxUsersFeature?.limit || 0),
                unit: maxUsersFeature?.limit <= 0 ? "user" : "users",
              },
              {
                label: "Remaining",
                value: Math.max(
                        maxUsersFeature.limit - (maxUsersFeature.used || 0),
                        0,
                      ).toLocaleString(),
                unit: maxUsersFeature?.limit <= 1 ? "user" : "users",
                highlight: true,
              },
              {
                label: "Usage",
                value: `${Math.round(
                  getUsagePercentage(
                    maxUsersFeature?.used || 0,
                    maxUsersFeature?.limit,
                  ),
                )}%`,
                unit: "of max users limit",
                accent: true,
              },
            ]}
            percentage={getUsagePercentage(
              maxUsersFeature?.used || 0,
              maxUsersFeature?.limit,
            )}
          />
        )}

        {monthlyEmailsFeature?.toggle && (
          <QuotaCard
            icon={ <img src={monthlyQuotaIcon} alt="monthlyQuotaIcon" />}
            title="Monthly Email Quota (Organization)"
            subtitle="Monitor and manage your monthly email usage."
            badge="Enabled"
            resetInfo={emailResetInfo}
            stats={[
              {
                label: "Emails Used",
                value: (monthlyEmailsFeature?.used || 0).toLocaleString(),
                unit: "email",
              },
              {
                label: "Email Limit (Monthly)",
                value:
                  monthlyEmailsFeature?.limit === -1
                    ? "Unlimited"
                    : (monthlyEmailsFeature?.limit || 0).toLocaleString(),
                unit: monthlyEmailsFeature?.limit === -1 ? "" : "emails / month",
              },
              {
                label: "Remaining",
                value:
                  monthlyEmailsFeature?.limit === -1
                    ? "Unlimited"
                    : Math.max(
                        monthlyEmailsFeature.limit -
                          (monthlyEmailsFeature.used || 0),
                        0,
                      ).toLocaleString(),
                unit: monthlyEmailsFeature?.limit === -1 ? "" : "emails",
                highlight: true,
              },
              {
                label: "Usage",
                value: `${Math.round(
                  getUsagePercentage(
                    monthlyEmailsFeature?.used || 0,
                    monthlyEmailsFeature?.limit,
                  ),
                )}%`,
                unit: "of monthly limit",
                accent: true,
              },
            ]}
            percentage={getUsagePercentage(
              monthlyEmailsFeature?.used || 0,
              monthlyEmailsFeature?.limit,
            )}
          />
        )}
      </div>
    </div>
  );
}

export default Billings;